import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import MobileTopBarClean from '../components/MobileTopBarClean';
import StockDetailsModal from '../components/StockDetailsModal';
import FloatingAIAssistant from '../components/FloatingAIAssistant';
import FloatingCompetitionButton from '../components/FloatingCompetitionButton';
import { useTranslation } from 'react-i18next';
import '../styles/global-white-backgrounds.css';
import '../styles/professional-investment-cards.css';
import '../styles/stocks-page.css';
import { useIsMobile } from '../hooks/useIsMobile';

const Stocks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getHeaderStats } = usePortfolioStats();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('ticker'); // 'ticker' or 'name'
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [draggedStock, setDraggedStock] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const dragElementRefs = useState(() => new Map())[0];

  // Fetch top 10 stocks by market value on component mount
  useEffect(() => {
    fetchTopStocks();
  }, []);


  // Setup non-passive touch events for mobile drag
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchMove = (e) => {
      if (!touchStartPos || !draggedStock) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.x);
      const deltaY = Math.abs(currentY - touchStartPos.y);
      
      // Very low threshold for easy mobile dragging
      if (deltaX > 5 || deltaY > 5) {
        if (!touchMoved) {
          setTouchMoved(true);
          setIsDragging(true);
          console.log('🎯 DRAG START: isDragging set to true');
        }
        
        // Update drag position for visual feedback with better tracking
        const dragX = currentX - touchStartPos.x;
        const dragY = currentY - touchStartPos.y;
        setDragPosition({ x: dragX, y: dragY });
        console.log('📍 DRAG POSITION: x:', dragX, 'y:', dragY);
        
        // Check if dragging over drop zone for visual feedback
        const elementAtPoint = document.elementFromPoint(currentX, currentY);
        const overDropZone = elementAtPoint?.closest('.icon-container') || 
                           elementAtPoint?.closest('.floating-ai-assistant');
        setIsOverDropZone(!!overDropZone);
        
        // Add haptic feedback on iOS when over drop zone
        if (overDropZone && navigator.vibrate) {
          navigator.vibrate(10);
        }
        
        e.preventDefault(); // This will now work with non-passive events
        e.stopPropagation();
      }
    };

    // Add non-passive touch event listeners to all draggable elements
    dragElementRefs.forEach((element) => {
      if (element) {
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
      }
    });

    return () => {
      dragElementRefs.forEach((element) => {
        if (element) {
          element.removeEventListener('touchmove', handleTouchMove);
        }
      });
    };
  }, [isMobile, touchStartPos, draggedStock, touchMoved, dragElementRefs]);

  // Debug visual states
  useEffect(() => {
    if (draggedStock) {
      console.log('🎨 VISUAL STATE:', {
        draggedStock,
        isDragging,
        dragPosition,
        isOverDropZone
      });
    }
  }, [draggedStock, isDragging, dragPosition, isOverDropZone]);

  const fetchTopStocks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Hardcoded list of the largest companies by market cap to ensure we get the actual biggest stocks
      const topTickers = [
        'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 
        'TSLA', 'META', 'TSM', 'AVGO', 'WMT'
      ];
      
      // Fetch data for each of the top stocks
      const stockPromises = topTickers.map(async (ticker) => {
        try {
          // Get previous day's data
          const prevResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}/prev`);
          const prevData = await prevResponse.json();
          
          // Get detailed company info
          const detailResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}`);
          const detailData = await detailResponse.json();
          
          const marketData = prevData.results?.[0] || {};
          const companyDetails = detailData.results || {};
          
          return {
            ticker: ticker,
            name: companyDetails.name || ticker,
            ask: marketData.h || 0, // Previous day high (ask)
            bid: marketData.l || 0, // Previous day low (bid)
            close: marketData.c || 0,
            volume: marketData.v || 0,
            marketCap: companyDetails.market_cap || 0,
            logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/logo`,
            iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/icon`
          };
        } catch (err) {
          console.error(`Error fetching data for ${ticker}:`, err);
          return {
            ticker: ticker,
            name: ticker,
            ask: 0,
            bid: 0,
            close: 0,
            volume: 0,
            marketCap: 0,
            logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/logo`,
            iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/icon`
          };
        }
      });
      
      const stocksWithData = await Promise.all(stockPromises);
      
      // Sort by market cap (biggest first) in case some data is missing
      const sortedStocks = stocksWithData.sort((a, b) => b.marketCap - a.marketCap);
      setStocks(sortedStocks);
      
    } catch (err) {
      setError('Error connecting to API: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchStocks = async () => {
    if (!searchTerm.trim()) {
      fetchTopStocks();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (searchType === 'ticker') {
        // For ticker search, try to fetch specific ticker directly
        const searchTermUpper = searchTerm.toUpperCase().trim();
        
        try {
          // Try exact match first
          const exactResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${searchTermUpper}`);
          const exactData = await exactResponse.json();
          
          if (exactData.status === 'OK' && exactData.results) {
            // Found exact match, get price data
            const prevResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${searchTermUpper}/prev`);
            const prevData = await prevResponse.json();
            
            const marketData = prevData.results?.[0] || {};
            const companyDetails = exactData.results;
            
            const result = {
              ticker: companyDetails.ticker,
              name: companyDetails.name || companyDetails.ticker,
              ask: marketData.h || 0,
              bid: marketData.l || 0,
              close: marketData.c || 0,
              volume: marketData.v || 0,
              marketCap: companyDetails.market_cap || 0,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${companyDetails.ticker}/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${companyDetails.ticker}/icon`
            };
            
            setStocks([result]);
            setError(null);
            return;
          }
        } catch (exactErr) {
          console.log('Exact match not found, trying partial search...');
        }
        
        // If exact match fails, try partial search with ticker prefix
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers?market=stocks&type=CS&active=true&ticker.gte=${searchTermUpper}&limit=20`);
        const data = await response.json();
        
        if (data.status === 'OK' && data.results) {
          // Filter results that start with search term
          const filteredResults = data.results.filter(stock => 
            stock.ticker.startsWith(searchTermUpper)
          ).slice(0, 10);
          
          if (filteredResults.length === 0) {
            setError(`No stocks found with ticker starting with "${searchTerm}"`);
            return;
          }
          
          // Process filtered results
          const stockPromises = filteredResults.map(async (stock) => {
            try {
              const prevResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}/prev`);
              const prevData = await prevResponse.json();
              
              const marketData = prevData.results?.[0] || {};
              
              return {
                ticker: stock.ticker,
                name: stock.name || stock.ticker,
                ask: marketData.h || 0,
                bid: marketData.l || 0,
                close: marketData.c || 0,
                volume: marketData.v || 0,
                marketCap: 0, // Will be populated if needed
                logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/logo`,
                iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/icon`
              };
            } catch (err) {
              return {
                ticker: stock.ticker,
                name: stock.name || stock.ticker,
                ask: 0,
                bid: 0,
                close: 0,
                volume: 0,
                marketCap: 0,
                logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/logo`,
                iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/icon`
              };
            }
          });
          
          const searchResults = await Promise.all(stockPromises);
          setStocks(searchResults);
        } else {
          setError(`No stocks found with ticker starting with "${searchTerm}"`);
        }
      } else {
        // For name search, fetch a broader list and filter by name
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers?market=stocks&type=CS&active=true&limit=100`);
        const data = await response.json();
        
        if (data.status === 'OK' && data.results) {
          // Filter by company name (case insensitive)
          const searchTermLower = searchTerm.toLowerCase();
          const filteredResults = data.results.filter(stock => 
            stock.name && stock.name.toLowerCase().includes(searchTermLower)
          ).slice(0, 10);
          
          if (filteredResults.length === 0) {
            setError(`No companies found with name containing "${searchTerm}"`);
            return;
          }
          
          // Process filtered results
          const stockPromises = filteredResults.map(async (stock) => {
            try {
              const prevResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}/prev`);
              const prevData = await prevResponse.json();
              
              const marketData = prevData.results?.[0] || {};
              
              return {
                ticker: stock.ticker,
                name: stock.name || stock.ticker,
                ask: marketData.h || 0,
                bid: marketData.l || 0,
                close: marketData.c || 0,
                volume: marketData.v || 0,
                marketCap: 0,
                logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/logo`,
                iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/icon`
              };
            } catch (err) {
              return {
                ticker: stock.ticker,
                name: stock.name || stock.ticker,
                ask: 0,
                bid: 0,
                close: 0,
                volume: 0,
                marketCap: 0,
                logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/logo`,
                iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/icon`
              };
            }
          });
          
          const searchResults = await Promise.all(stockPromises);
          setStocks(searchResults);
        } else {
          setError('Search failed - unable to fetch stock data');
        }
      }
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchStocks();
  };


  const formatCurrency = (value) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatPrice = (value) => {
    return `$${value.toFixed(2)}`;
  };

  const handleStockClick = (stock) => {
    setSelectedStock(stock);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedStock(null);
  };

  return (
    <div className="dashboard-container jpx-style">
      <div className="main-content" style={{ marginLeft: 0, width: '100%' }}>
        {/* X BUTTON TO RETURN TO DASHBOARD */}
        <button className="modal-close" onClick={() => navigate('/dashboard')} style={{
          position: 'fixed',
          top: isMobile ? '80px' : '180px',
          right: '20px',
          width: '40px',
          height: '40px',
          background: '#000000',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '24px',
          fontWeight: 'bold',
          cursor: 'pointer',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }} 
        onMouseEnter={(e) => {
          e.target.style.background = '#D72638';
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = '#000000';
          e.target.style.transform = 'scale(1)';
        }}
        title={t('common:navigation.backToDashboard')}>×</button>

        {/* Conditional Header - Mobile vs Desktop */}
        <MobileTopBarClean stats={getHeaderStats()} />

        {/* Main content */}
        <main className="dashboard-content" style={{
          marginTop: isMobile ? '75px' : '0'
        }}>
          {/* Search Section */}
          <section className="search-section">
            <div className="section-header">
              <h2 className="section-title">{t('assets:pages.stocks.searchTitle')}</h2>
              <div className="section-subtitle">{t('assets:pages.stocks.searchSubtitle')}</div>
            </div>
            
            <form onSubmit={handleSearchSubmit} className="search-form">
              <div className="search-controls">
                <div className="search-type-selector">
                  <label className="search-type-option">
                    <input
                      type="radio"
                      value="ticker"
                      checked={searchType === 'ticker'}
                      onChange={(e) => setSearchType(e.target.value)}
                    />
                    <span>{t('assets:search.types.ticker')}</span>
                  </label>
                  <label className="search-type-option">
                    <input
                      type="radio"
                      value="name"
                      checked={searchType === 'name'}
                      onChange={(e) => setSearchType(e.target.value)}
                    />
                    <span>{t('assets:search.types.name')}</span>
                  </label>
                </div>

                <div className="search-input-group">
                  <input
                    type="text"
                    className="search-input"
                    placeholder={searchType === 'ticker' ? t('assets:search.placeholders.ticker') : t('assets:search.placeholders.companyName')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit" className="search-button" disabled={loading}>
                    {loading ? t('assets:search.searching') : t('assets:search.button')}
                  </button>
                </div>
              </div>
            </form>
          </section>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon"></span>
              {error}
            </div>
          )}

          {/* Stocks Grid */}
          <section className="stocks-section">
            <div className="section-header">
              <h2 className="section-title">
                {searchTerm ? t('assets:pages.stocks.resultsTitle') : t('assets:pages.stocks.topStocksTitle')}
              </h2>
              <div className="section-subtitle">
                {t('assets:pages.stocks.topStocksSubtitle')}
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{t('assets:loading.stockData')}</p>
              </div>
            ) : (
              <div className="stocks-grid">
                {stocks.map((stock) => (
                  <div 
                    key={stock.ticker}
                    className="stock-card stocks"
                    onClick={(e) => {
                      // Don't trigger click if we're dragging
                      if (!touchMoved && !draggedStock) {
                        handleStockClick(stock);
                      }
                    }}
                  >
                    {/* Stock Logo - Draggable */}
                    <div 
                      ref={(el) => {
                        if (el) {
                          dragElementRefs.set(stock.ticker, el);
                        }
                      }}
                      className={`stock-logo draggable-logo ${
                        draggedStock === stock.ticker ? 'dragging-active' : ''
                      } ${isDragging && draggedStock === stock.ticker ? 'dragging-move' : ''}`}
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          ticker: stock.ticker,
                          name: stock.name,
                          price: stock.close,
                          type: 'stock'
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setDraggedStock(stock.ticker);
                      }}
                      onDragEnd={(e) => {
                        setDraggedStock(null);
                      }}
                      // Mobile touch support - only activate on clear drag intent
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        const touch = e.touches[0];
                        setTouchStartPos({
                          x: touch.clientX,
                          y: touch.clientY,
                          time: Date.now(),
                          pageX: touch.pageX,
                          pageY: touch.pageY
                        });
                        setTouchMoved(false);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        
                        // Provide immediate visual feedback
                        setDraggedStock(stock.ticker);
                        console.log('👆 TOUCH START: Set draggedStock to', stock.ticker);
                      }}
                      onTouchMove={(e) => {
                        // This is now handled by the non-passive event listener in useEffect
                        // Just prevent default scrolling behavior here
                        if (touchStartPos && draggedStock === stock.ticker) {
                          e.stopPropagation();
                        }
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        
                        if (!touchStartPos) {
                          return;
                        }
                        
                        const touchDuration = Date.now() - touchStartPos.time;
                        const wasLongPress = touchDuration > 500; // Shorter hold for mobile
                        
                        
                        // Only trigger if user dragged to a drop zone (not just drag/hold)
                        if (touchMoved) {
                          // Check if touch ended over the AI assistant drop zone
                          const touchEndX = e.changedTouches[0].clientX;
                          const touchEndY = e.changedTouches[0].clientY;
                          const elementAtEnd = document.elementFromPoint(touchEndX, touchEndY);
                          
                          // Look for the AI assistant drop zone or its children
                          const dropZone = elementAtEnd?.closest('.icon-container') || 
                                          elementAtEnd?.closest('.floating-ai-assistant');
                          
                          if (dropZone) {
                            console.log('✅ MOBILE DRAG: Dispatching event for', stock.ticker);
                            const event = new CustomEvent('tickerDropped', {
                              detail: {
                                ticker: stock.ticker,
                                name: stock.name,
                                price: stock.close,
                                type: 'stock'
                              }
                            });
                            window.dispatchEvent(event);
                            console.log('✅ MOBILE DRAG: Event dispatched successfully');
                          } else {
                            console.log('❌ MOBILE DRAG: Not over drop zone. Element:', elementAtEnd?.className);
                          }
                        }
                        
                        // Reset all touch state
                        setTouchStartPos(null);
                        setTouchMoved(false);
                        setDraggedStock(null);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        setIsOverDropZone(false);
                      }}
                      style={{ 
                        cursor: 'grab',
                        opacity: draggedStock === stock.ticker ? (isDragging ? 0.8 : 0.9) : 1,
                        '--drag-x': isDragging && draggedStock === stock.ticker ? `${dragPosition.x}px` : '0px',
                        '--drag-y': isDragging && draggedStock === stock.ticker ? `${dragPosition.y}px` : '0px',
                        '--drag-scale': draggedStock === stock.ticker ? 
                          (isDragging ? (isOverDropZone ? '1.25' : '1.15') : '1.05') : '1',
                        '--drag-rotate': isDragging && draggedStock === stock.ticker ? '2deg' : '0deg',
                        boxShadow: draggedStock === stock.ticker ? '0 8px 20px rgba(0,0,0,0.3)' : 'none',
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        touchAction: 'none',
                        WebkitUserDrag: 'element',
                        userSelect: 'none',
                        backgroundColor: draggedStock === stock.ticker ? 
                          (isOverDropZone ? '#e0f2fe' : '#f0f0f0') : 'transparent',
                        borderRadius: '8px',
                        zIndex: draggedStock === stock.ticker ? 1000 : 1
                      }}
                      title={isMobile ? t('assets:card.mobileDragHint') : t('assets:card.dragHint')}
                    >
                      <img 
                        src={stock.logoUrl}
                        alt={`${stock.name} logo`}
                        style={{ 
                          pointerEvents: 'none',
                          touchAction: 'none', // Disable browser touch handling
                          userSelect: 'none', // Disable selection
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none' // Disable iOS callout
                        }}
                        onError={(e) => {
                          // Suppress console error by preventing default error handling
                          e.preventDefault();
                          
                          // First fallback: try icon instead of logo (only if not already tried)
                          if (e.target.src === stock.logoUrl && stock.iconUrl !== stock.logoUrl) {
                            e.target.src = stock.iconUrl;
                          } else {
                            // Final fallback: create a clean SVG placeholder
                            const placeholder = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                              `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
                                <rect width="100" height="100" fill="#059669" rx="16"/>
                                <text x="50" y="60" font-family="Arial, sans-serif" font-size="36" font-weight="bold" fill="white" text-anchor="middle">${stock.ticker[0]}</text>
                               </svg>`
                            )}`;
                            e.target.src = placeholder;
                          }
                        }}
                      />
                    </div>

                    {/* Stock Info */}
                    <div className="stock-info">
                      <div className="stock-header">
                        <h3 className="stock-ticker">{stock.ticker}</h3>
                        <div className="stock-price">{formatPrice(stock.close)}</div>
                      </div>
                      
                      <div className="stock-name">{stock.name}</div>
                      
                      <div className="stock-metrics">
                        <div className="metric-row">
                          <span className="metric-label">{t('assets:card.labels.askDayHigh')}</span>
                          <span className="metric-value ask">{formatPrice(stock.ask)}</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">{t('assets:card.labels.bidDayLow')}</span>
                          <span className="metric-value bid">{formatPrice(stock.bid)}</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">{t('assets:card.labels.marketCap')}</span>
                          <span className="metric-value">{formatCurrency(stock.marketCap)}</span>
                        </div>
                        <div className="metric-row">
                          <span className="metric-label">{t('assets:card.labels.volume')}</span>
                          <span className="metric-value">{stock.volume.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      className="stock-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStockClick(stock);
                      }}
                    >
                      {t('assets:card.actions.viewDetails')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!loading && stocks.length === 0 && !error && (
              <div className="empty-state">
                <div className="empty-icon">📈</div>
                <h3>{t('assets:errors.noStocksFound')}</h3>
                <p>{t('assets:errors.tryAdjusting')}</p>
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Stock Details Modal */}
      <StockDetailsModal 
        stock={selectedStock}
        isOpen={modalOpen}
        onClose={handleCloseModal}
      />

      {/* Floating AI Assistant */}
      <FloatingAIAssistant />

      {/* Floating Competition Button */}
      <FloatingCompetitionButton />
    </div>
  );
};

export default Stocks;